/**
 * Names the mapping workspace's navigation destinations so the shared guard can
 * tell the user where a request would take them. Nothing here navigates or
 * decides anything — it only turns a tab index, setup screen, or profile key
 * into the destination vocabulary the guard understands.
 */

import type { NavigationRequest, NavigationSource } from '../../../shared/lib/setupRevision';
import {
  toMappingWorkspaceTabKey,
  type MappingWorkspaceTabIndex,
  type MappingWorkspaceTabKey,
} from './mappingWorkspacePageModel';

/** Short tab names, as they read inside a sentence in the unsaved-changes prompt. */
const WorkspaceTabLabel: Record<MappingWorkspaceTabKey, string> = {
  mapping: 'Mapping',
  quickFill: 'Quick Fill',
  validation: 'Validation',
  export: 'Export',
};

export const workspaceTabDestination = (
  tab: MappingWorkspaceTabIndex,
  source: NavigationSource
): NavigationRequest => {
  const target = toMappingWorkspaceTabKey(tab);

  return {
    source,
    kind: 'workspaceTab',
    target,
    label: WorkspaceTabLabel[target],
  };
};

/** The setup screen a Back button or the workspace summary returns to. */
export const setupBackDestination = (
  source: NavigationSource,
  target: string
): NavigationRequest => ({
  source,
  kind: 'setupBack',
  target,
});

export const profileDestination = (
  source: NavigationSource,
  profileKey: string,
  profileName?: string
): NavigationRequest => ({
  source,
  kind: 'profile',
  target: profileKey,
  label: profileName,
});
